import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Generator from "./pages/Generator";
import Preview from "./pages/Preview";
import Pricing from "./pages/Pricing";
import PaymentSuccess from "./pages/PaymentSuccess";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import RefundPolicy from "./pages/RefundPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import About from "./pages/About";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import MyLibrary from "./pages/MyLibrary";
import Blog from "./pages/Blog";
import ArticleColoringPagePrompts from "./pages/blog/ArticleColoringPagePrompts";
import ArticleAIColoringBookTools from "./pages/blog/ArticleAIColoringBookTools";
import ArticleCreateColoringPagesWithAI from "./pages/blog/ArticleCreateColoringPagesWithAI";
import ArticlePrintableColoringPagesGenerator from "./pages/blog/ArticlePrintableColoringPagesGenerator";
import ArticleCustomColoringPages from "./pages/blog/ArticleCustomColoringPages";
// New SEO Articles (Dec 2025)
import ArticleHowToMakeColoringBook2026 from "./pages/blog/ArticleHowToMakeColoringBook2026";
import ArticleColoringBookToSell2026 from "./pages/blog/ArticleColoringBookToSell2026";
import Article50BestPrompts2026 from "./pages/blog/Article50BestPrompts2026";
import ArticleAmazonKDPPublishing2026 from "./pages/blog/ArticleAmazonKDPPublishing2026";
import ArticleMakeYourOwnColoringBookFree2026 from "./pages/blog/ArticleMakeYourOwnColoringBookFree2026";
// Printable Coloring Pages Articles (Feb 2025)
import ArticlePawPatrolSkye from "./pages/blog/ArticlePawPatrolSkye";

// Free Tools Pages
import FreeTools from "./pages/FreeTools";
import NameColoringGenerator from "./pages/tools/NameColoringGenerator";
import BubbleLetterGenerator from "./pages/tools/BubbleLetterGenerator";
import QuoteColoringGenerator from "./pages/tools/QuoteColoringGenerator";

// Premium Features
import PhotoToColoring from "./pages/PhotoToColoring";

// Admin (Phase 1 + 2)
import AdminLayout from "./pages/admin/AdminLayout";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminAuditLog from "./pages/admin/AdminAuditLog";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminContent from "./pages/admin/AdminContent";
import AdminFailedSignups from "./pages/admin/AdminFailedSignups";
import AdminPlaceholder from "./pages/admin/AdminPlaceholder";

// Components
import CookieConsent from "./components/CookieConsent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with auth interceptor for mobile compatibility
const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Add request interceptor to include auth token from localStorage
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('colorwonder_session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
export const AuthContext = createContext(null);

// Export API and apiClient for use in other components
export { API, apiClient };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper to get auth headers (uses localStorage token as fallback)
  const getAuthHeaders = () => {
    const token = localStorage.getItem('colorwonder_session_token');
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  };

  const checkAuth = async () => {
    // Skip if in the middle of an OAuth callback - AuthCallback handles this
    if (window.location.hash?.includes('session_id=') || window.location.href?.includes('session_id=')) {
      console.log("checkAuth: Skipping - OAuth callback in progress");
      setLoading(false);
      return;
    }
    try {
      console.log("Checking auth with API:", `${API}/auth/me`);
      
      // Get token from localStorage (most reliable across browser sessions)
      const token = localStorage.getItem('colorwonder_session_token');
      
      let response;
      let authMethod = 'none';
      
      // Try with localStorage token first (more reliable than cookies)
      if (token) {
        try {
          console.log("Trying auth with localStorage token");
          response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          });
          authMethod = 'token';
        } catch (tokenError) {
          console.log("Token auth failed, trying cookies only");
          // Token might be expired, try cookies
          try {
            response = await axios.get(`${API}/auth/me`, {
              withCredentials: true,
            });
            authMethod = 'cookie';
          } catch (cookieError) {
            throw tokenError; // Use original error
          }
        }
      } else {
        // No token, try cookies only
        response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
        });
        authMethod = 'cookie';
      }
      
      console.log(`Auth check successful (${authMethod}):`, response.data?.email, "tier:", response.data?.subscription_tier);
      setUser(response.data);
      
      // Store/update session token if returned
      if (response.data?.session_token) {
        localStorage.setItem('colorwonder_session_token', response.data.session_token);
      }
      
      // Store user data for persistence
      if (response.data?.email) {
        localStorage.setItem('colorwonder_last_user', JSON.stringify({
          email: response.data.email,
          name: response.data.name,
          subscription_tier: response.data.subscription_tier,
          user_id: response.data.user_id,
          timestamp: new Date().toISOString()
        }));
      }
    } catch (error) {
      console.error("Auth check failed:", error.response?.status, error.response?.data?.detail);
      
      // Log last known user for debugging
      const lastUser = localStorage.getItem('colorwonder_last_user');
      if (lastUser) {
        console.log("Last known user (from localStorage):", lastUser);
      }
      
      // Clear invalid token
      localStorage.removeItem('colorwonder_session_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    checkAuth();
  }, []);

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    const authUrl = process.env.REACT_APP_AUTH_URL || "https://auth.emergentagent.com";
    window.location.href = `${authUrl}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      // Clear localStorage tokens
      localStorage.removeItem('colorwonder_session_token');
      localStorage.removeItem('colorwonder_last_user');
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      // Still clear local state even if API fails
      localStorage.removeItem('colorwonder_session_token');
      localStorage.removeItem('colorwonder_last_user');
      setUser(null);
    }
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginWithGoogle, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate("/");
        return;
      }

      const sessionId = sessionIdMatch[1];
      console.log("Processing Google auth callback with session_id");

      // Retry logic — only retry on 5xx/network errors. 
      // Session_id is one-time use, so 401/404 means expired; retrying won't help.
      const maxRetries = 3;
      let lastError = null;
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`Auth attempt ${attempt}/${maxRetries}`);
          
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { 
              withCredentials: true,
              timeout: 30000 // 30 second timeout
            }
          );
          
          console.log("Auth session response:", response.data?.email, "tier:", response.data?.subscription_tier);
          
          // Store session token in localStorage as fallback for browsers that block cookies
          if (response.data?.session_token) {
            localStorage.setItem('colorwonder_session_token', response.data.session_token);
            console.log("Session token stored in localStorage");
          }
          
          // Store user info for session persistence
          if (response.data?.email) {
            localStorage.setItem('colorwonder_last_user', JSON.stringify({
              email: response.data.email,
              name: response.data.name,
              subscription_tier: response.data.subscription_tier,
              user_id: response.data.user_id,
              timestamp: new Date().toISOString()
            }));
          }
          
          setUser(response.data);
          toast.success(`Welcome, ${response.data.name}!`);
          navigate("/dashboard", { replace: true, state: { user: response.data } });
          return; // Success - exit the retry loop
          
        } catch (error) {
          lastError = error;
          console.error(`Auth attempt ${attempt} failed:`, error.response?.data || error.message);
          
          // 4xx errors: don't retry (session_id is one-time use)
          if (error.response?.status >= 400 && error.response?.status < 500) {
            const detail = error.response?.data?.detail || "";
            if (error.response?.status === 400) {
              toast.error(detail || "Unable to create account. Please try a different method.");
            } else {
              toast.error("Google sign-in session expired. Please try again.");
            }
            navigate("/login", { replace: true });
            return;
          }
          
          // Only retry on 5xx/network errors
          if (attempt < maxRetries) {
            const waitTime = attempt * 1000;
            console.log(`Waiting ${waitTime}ms before retry...`);
            await new Promise(resolve => setTimeout(resolve, waitTime));
          }
        }
      }
      
      // All retries failed (only happens for 5xx/network errors)
      console.error("All auth attempts failed:", lastError);
      toast.error("Could not connect to authentication server. Please try again.");
      navigate("/login", { replace: true });
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-hero-gradient">
      <div className="text-center">
        <div className="loader mx-auto mb-4"></div>
        <p className="text-slate-600">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate("/login", { replace: true });
    }
  }, [user, loading, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hero-gradient">
        <div className="loader"></div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return null;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL hash - handle auth callback
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/refund" element={<RefundPolicy />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/coloring-page-prompts" element={<ArticleColoringPagePrompts />} />
      <Route path="/blog/ai-coloring-book-generator-tools" element={<ArticleAIColoringBookTools />} />
      <Route path="/blog/create-coloring-pages-with-ai" element={<ArticleCreateColoringPagesWithAI />} />
      <Route path="/blog/printable-coloring-pages-generator" element={<ArticlePrintableColoringPagesGenerator />} />
      <Route path="/blog/custom-coloring-pages" element={<ArticleCustomColoringPages />} />
      {/* New SEO Articles (Dec 2025) */}
      <Route path="/blog/how-to-make-coloring-book" element={<ArticleHowToMakeColoringBook2026 />} />
      <Route path="/blog/coloring-book-to-sell" element={<ArticleColoringBookToSell2026 />} />
      <Route path="/blog/best-coloring-page-prompts" element={<Article50BestPrompts2026 />} />
      <Route path="/blog/amazon-kdp-publishing" element={<ArticleAmazonKDPPublishing2026 />} />
      <Route path="/blog/make-your-own-coloring-book" element={<ArticleMakeYourOwnColoringBookFree2026 />} />
      {/* Printable Coloring Pages */}
      <Route path="/blog/paw-patrol-skye-coloring-pages" element={<ArticlePawPatrolSkye />} />
      {/* Free Tools Routes */}
      <Route path="/free-tools" element={<FreeTools />} />
      <Route path="/free-tools/name-coloring-generator" element={<NameColoringGenerator />} />
      <Route path="/free-tools/bubble-letter-generator" element={<BubbleLetterGenerator />} />
      <Route path="/free-tools/quote-coloring-generator" element={<QuoteColoringGenerator />} />
      {/* Premium Features */}
      <Route
        path="/photo-to-coloring"
        element={
          <ProtectedRoute>
            <PhotoToColoring />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/generate"
        element={
          <ProtectedRoute>
            <Generator />
          </ProtectedRoute>
        }
      />
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <MyLibrary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/preview/:projectId"
        element={
          <ProtectedRoute>
            <Preview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      {/* Admin dashboard — Phase 1. Gate enforced inside AdminLayout (strict is_admin check). */}
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminOverview />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="audit-log" element={<AdminAuditLog />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="failed-signups" element={<AdminFailedSignups />} />
        <Route path="system-health" element={<AdminPlaceholder title="System Health" phase="3" />} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster 
          position="top-center" 
          richColors 
          toastOptions={{
            style: {
              fontFamily: "'Outfit', sans-serif"
            }
          }}
        />
        <AppRouter />
        <CookieConsent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
